import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';
import { describeEvent, dayLabel, relativeTime, toMillis, formatDateTime, EVENT_GROUPS } from './sessionsUtils';

const ACTIVITY_PAGE = 30;

/**
 * Audit log (securityEvents, written by /api/sessions), loaded lazily: the
 * newest page when the tab opens, older pages only on "Load older" (a
 * startAfter cursor). No listener, so a look costs ≤30 reads, not a read per
 * event all day.
 */
export default function SecurityActivity({ now }) {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null); // last doc of the loaded pages
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState('loading'); // loading | more | ready | error
  const [error, setError] = useState('');
  const [group, setGroup] = useState('all');

  const load = useCallback(async (after = null) => {
    setState(after ? 'more' : 'loading');
    try {
      const parts = [collection(db, 'securityEvents'), orderBy('at', 'desc')];
      if (after) parts.push(startAfter(after));
      parts.push(limit(ACTIVITY_PAGE));
      const snap = await getDocs(query(...parts));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEvents((prev) => (after ? [...prev, ...rows] : rows));
      setCursor(snap.docs[snap.docs.length - 1] || after);
      setHasMore(snap.docs.length === ACTIVITY_PAGE);
      setState('ready');
    } catch (err) {
      setError(err.code === 'permission-denied'
        ? 'Activity needs the latest Firestore rules — run: firebase deploy --only firestore:rules'
        : `Could not load activity: ${err.message}`);
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const types = EVENT_GROUPS.find((g) => g.key === group)?.types;
    return types ? events.filter((e) => types.includes(e.type)) : events;
  }, [events, group]);

  const byDay = useMemo(() => {
    const out = [];
    for (const e of shown) {
      const label = dayLabel(toMillis(e.at) || now, now);
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, items: [] });
      out[out.length - 1].items.push(e);
    }
    return out;
  }, [shown, now]);

  return (
    <div className="ses__activity">
      <div className="ses__toolbar">
        <div className="ses__chips" role="group" aria-label="Filter activity">
          {EVENT_GROUPS.map((g) => (
            <button key={g.key} type="button" className={`ses__chip${group === g.key ? ' is-on' : ''}`} aria-pressed={group === g.key} onClick={() => setGroup(g.key)}>
              {g.label}
            </button>
          ))}
        </div>
        <button type="button" className="ses__btn ses__btn--sm" onClick={() => load()} disabled={state === 'loading'}>
          {state === 'loading' ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {state === 'error' && <div className="ses__error">{error}</div>}
      {state === 'loading' && !events.length && <div className="ses__skeleton" aria-hidden="true"><span /><span /><span /></div>}
      {state === 'ready' && !shown.length && !hasMore && (
        <p className="ses__empty">No activity yet. Sign-ins, sign-outs, password changes and blocks will show up here.</p>
      )}

      {byDay.map((day) => (
        <section key={day.label} className="ses__day">
          <h3 className="ses__day-label">{day.label}</h3>
          <ol className="ses__events">
            {day.items.map((e) => {
              const d = describeEvent(e);
              const at = toMillis(e.at);
              return (
                <li key={e.id} className={`ses__event ses__event--${d.tone}`}>
                  <span className="ses__event-icon" aria-hidden="true">{d.icon}</span>
                  <div className="ses__event-body">
                    <div className="ses__event-text">{d.text}</div>
                    {e.place && <div className="ses__event-meta">{e.place}</div>}
                  </div>
                  <time className="ses__event-time" dateTime={at ? new Date(at).toISOString() : undefined} title={formatDateTime(at)}>
                    {at ? relativeTime(at, now) : 'just now'}
                  </time>
                </li>
              );
            })}
          </ol>
        </section>
      ))}

      {events.length > 0 && (
        <div className="ses__more">
          {hasMore ? (
            <button type="button" className="ses__btn" onClick={() => load(cursor)} disabled={state === 'more'}>
              {state === 'more' ? 'Loading…' : 'Load older activity'}
            </button>
          ) : (
            <span>That’s everything — activity is kept for 90 days.</span>
          )}
        </div>
      )}
    </div>
  );
}
